import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  IconButton,
  Modal,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ListingTable from "./listing";
import BasicBreadcrumbs from "../../components/breadcrumb";
import PrivateLayout from "../../layouts/privateLayout";
import { Add, Close, Delete } from "@mui/icons-material";
import toast from "react-hot-toast";
import { useApiClient } from "../../context/ApiClientContext";
import UploadUserButton from "./uploadUserButton";
import ComponentBackdrop from "../../components/backdrop";
import api from "../../helper/baseApi";
import DetailsUserForm from "./detailsUserForm";
import SuccessAlert from "../../helper/successAlert";
import ListingGrid from "./listingGrid";
import ConfirmDialog from "../../helper/confirmDialog";
import ErrorAlert from "../../helper/errorAlert";
import { useNavigate } from "react-router-dom";

export const DataContext = React.createContext();

function Index() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showButton, setShowButton] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [filteredDataList, setFilteredDataList] = React.useState([]);

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [openConfirm, setOpenConfirm] = useState(false);

  const { apiUrls: { mobileApiUrl } } = useApiClient();
  const navigate = useNavigate();

  const handleOpenConfirm = () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select at least one department to delete.");
      return;
    }
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);


  const fetchDataAndUpdateList = async (page) => {
    try {
      const apiUrl = `${mobileApiUrl}user/user-employee-list`;
      const response = await api.get(apiUrl);
      const users = response.data.data.userEmployeeList;
      const dataWithKeys = users.map((item) => ({
        ...item,
        id: item.employeeId
      }));

      setFilteredDataList(dataWithKeys);
    } catch (error) {
      if (error?.response?.status === 404) {
        setFilteredDataList([]);
      } else {
        console.error("An error occurred:", error?.response?.statusText);
      }
    }
  };

  useEffect(() => {
    fetchDataAndUpdateList();
  }, []);

  const handleShowButton = (ids) => {
    setSelectedIds(ids);
    setShowButton(ids.length > 0);
  };

  const handleClose = () => {
    setShowAddModal(false);
  };

  const openAddUserModal = (data) => {
    setShowAddModal(true);
  };

  const handleConfirmDelete = async () => {
    setOpenBackdrop(true);
    try {
      const response = await api.put(
        `${mobileApiUrl}user/deactive-user`,
        { employeeIds: selectedIds }
      );

      if (response.status === 200) {
        const deletedItems = filteredDataList.filter(item =>
          selectedIds.includes(item.id)
        );
        const deletedNames = deletedItems.map(item => item.name);
        setTimeout(() => {
          toast.success(`Deleted: ${deletedNames.join(", ")}`);

          setFilteredDataList(prev =>
            prev.filter(item => !selectedIds.includes(item.id))
          );
          setSelectedIds([]);
          setOpenConfirm(false);

        }, 2000);
      } else {
        toast.error("Failed to delete departments. Please try again.");
      }
    } catch (error) {
      setTimeout(() => {
        ErrorAlert(error, navigate);
      }, 2000);
    } finally {
      setTimeout(() => {
        setOpenBackdrop(false);
      }, 2000);
    }
  };

  return (
    <PrivateLayout>
      <ComponentBackdrop openBackdrop={openBackdrop} />
      <Box sx={{ mx: "40px", mt: 5 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <BasicBreadcrumbs first="User Management" />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "flex-start" },
            }}
          >
            {showButton && (
              <Button
                startIcon={<Delete />}
                variant="contained"
                size="large"
                fullWidth={isMobile}
                sx={{
                  backgroundColor: "secondary.main",
                  fontWeight: "600",
                  "&:hover": { backgroundColor: "secondary.dark" },
                  whiteSpace: "nowrap",
                }}
                onClick={handleOpenConfirm}
              >
                Delete
              </Button>
            )}

            <ConfirmDialog
              open={openConfirm}
              onClose={handleCloseConfirm}
              onConfirm={handleConfirmDelete}
              title="Delete User"
              message={`Are you sure you want to delete ${selectedIds.length > 1 ? "these users" : "this user"
                }?`}
              items={filteredDataList
                .filter(item => selectedIds.includes(item.id))
                .map(item => item.name)}
              confirmText="Delete"
              confirmColor="error"
            />

            <UploadUserButton refreshData={fetchDataAndUpdateList} flag={'user'} />
            <Button
              display="none"
              variant="contained"
              size="large"
              startIcon={<Add />}
              fullWidth={isMobile}
              onClick={openAddUserModal}
              sx={{
                backgroundColor: "secondary.main",
                fontWeight: "600",
                "&:hover": { backgroundColor: "secondary.dark" },
                whiteSpace: "nowrap",
                hidden: "true",
                fontSize: 14,
              }}
            >
              Add User
            </Button>

            <Modal
              open={showAddModal}
              onClose={handleClose}
              closeAfterTransition
              slots={{ backdrop: Backdrop }}
              slotProps={{ backdrop: { timeout: 500 } }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Fade in={showAddModal} timeout={500}>
                <Box
                  sx={{
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    overflowY: "auto",
                    position: "relative",
                    width: { xs: "90%", sm: "70%" },
                    maxHeight: "90vh",
                  }}
                >
                  <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 12,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <Close />
                  </IconButton>

                  <Box>
                    {/* <BasicBreadcrumbs first="User Management" /> */}
                    <DetailsUserForm
                      mode={"create"}
                      onModeChange={setShowAddModal}
                      onSuccess={fetchDataAndUpdateList}
                    />
                    {/* <FormDetail submitForm={submitForm} onClose={handleClose} /> */}
                  </Box>
                </Box>
              </Fade>
            </Modal>
          </Stack>
        </Box>
        <DataContext.Provider value={filteredDataList}>
          <ListingTable
            isSelected={false}
            isSoftDelete={false}
            handleShowButton={handleShowButton}
            openAddUserModal={openAddUserModal}
            refreshData={fetchDataAndUpdateList}
          />
          {/* <ListingGrid
            isSelected={false}
            isSoftDelete={false}
            handleShowButton={handleShowButton}
            openAddUserModal={openAddUserModal}
            refreshData={fetchDataAndUpdateList}
          /> */}
        </DataContext.Provider>
      </Box>
    </PrivateLayout>
  );
}

export default Index;
